import { db, auth } from "@/firebaseConfig";
import { doc, setDoc, increment, serverTimestamp, collection, getDocs } from "firebase/firestore";
import { noteFailure } from "@/utils/silentFailure";

// Records page-view telemetry into
//   users/{uid}/pageViews/{YYYY-MM-DD}
// One doc per user per day with a counter, the last viewed route, and
// a server timestamp. Lets the admin dashboard see "passive" activity
// (members who log in just to browse without clicking anything).
// Members write their own; admins read all.

const DEBOUNCE_MS = 1000;
let lastWriteAt = 0;

function dateKey() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

// Strip query string and hash before logging. Even though YoungPro
// routes are mostly safe, defensive against ever leaking tokens or
// emails into Firestore via a future ?param=value pattern.
function sanitize(path) {
  if (!path || typeof path !== "string") return "";
  return path.split("?")[0].split("#")[0];
}

async function log(routePath) {
  const uid = auth.currentUser?.uid;
  const cleanPath = sanitize(routePath);
  if (!uid || !cleanPath) return;
  // Debounce: rapid history nav (back/forward, swipe) shouldn't spam
  // Firestore. One write per second is plenty for activity tracking.
  const now = Date.now();
  if (now - lastWriteAt < DEBOUNCE_MS) return;
  lastWriteAt = now;
  try {
    const ref = doc(db, "users", uid, "pageViews", dateKey());
    await setDoc(
      ref,
      {
        count: increment(1),
        lastRoute: cleanPath,
        lastViewedAt: serverTimestamp(),
        date: dateKey(),
      },
      { merge: true }
    );
  } catch (e) {
    // Still swallowed — telemetry must never block navigation — but no longer
    // INVISIBLE. This exact path was dead for months behind a missing rules
    // block with zero symptoms.
    noteFailure('pageViewService.log', e);
  }
}

/**
 * The member's visit-day keys (doc ids, YYYY-MM-DD).
 *
 * NOTE (2026-07-30): the 🔥 attendance streak that used to consume this was
 * removed — it rewarded opening the app, which can rise while a member learns
 * nothing. pageViews is STILL written on every navigation and is still read by
 * adminAnalyticsService (last-active, including members who browse without
 * clicking), so neither the collection nor its rules block goes away.
 *
 * Fail-soft: [].
 * @param {string} uid
 * @returns {Promise<string[]>}
 */
async function fetchMyDates(uid) {
  if (!uid) return [];
  try {
    const snap = await getDocs(collection(db, "users", String(uid), "pageViews"));
    return snap.docs.map((d) => d.id);
  } catch {
    return [];
  }
}

export default { log, fetchMyDates, dateKey };
